import { useEffect, useState } from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import {
    createAppraisal,
    updateAppraisal,
} from "services/api/appraisalApi";

import {
    getSchools,
} from "services/api/schoolApi";

import {
    showSuccess,
    showError,
} from "utils/toast";


const DEFAULT_FORM = {
    schoolId: "",
    visitId: "",
    appraisalDate: "",
    competitor: "",
    discount: "",
    expectedOrder: "",
    decisionMaker: "",
    samplesGiven: "",
    remarks: "",
    followUpDate: "",
    books: [],
};


const EMPTY_BOOK = {
    bookName: "",
    isbn: "",
    discussed: true,
    finalized: false,
    quantity: 1,
    unitPrice: "",
    discount: "",
    remarks: "",
};


export default function AppraisalModal({
    open,
    onClose,
    reload,
    item,
}) {

    const isEdit = Boolean(item);


    const [loading, setLoading] =
        useState(false);


    const [schoolsLoading, setSchoolsLoading] =
        useState(false);


    const [schools, setSchools] =
        useState([]);


    const [form, setForm] =
        useState(DEFAULT_FORM);


    /*
    |--------------------------------------------------------------------------
    | RESPONSE HELPERS
    |--------------------------------------------------------------------------
    */

    const extractSchools = (response) => {

        return (
            response?.data?.schools ||
            response?.data?.data?.schools ||
            response?.data?.records ||
            response?.data?.data?.records ||
            response?.schools ||
            []
        );

    };


    /*
    |--------------------------------------------------------------------------
    | LOAD SCHOOLS
    |--------------------------------------------------------------------------
    */

    const loadSchools = async () => {

        try {

            setSchoolsLoading(true);


            const response =
                await getSchools(
                    1,
                    500
                );


            const records =
                extractSchools(
                    response
                );


            setSchools(
                Array.isArray(records)
                    ? records
                    : []
            );


        } catch (error) {

            console.error(
                "Schools loading error:",
                error
            );


            setSchools([]);


            showError(
                error?.response?.data?.message ||
                "Unable to load schools."
            );

        } finally {

            setSchoolsLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | EDIT FORM
    |--------------------------------------------------------------------------
    */

    const buildEditForm = (
        appraisal
    ) => {

        return {

            schoolId:
                appraisal?.school?.id ||
                appraisal?.schoolId ||
                "",

            visitId:
                appraisal?.visit?.id ||
                appraisal?.visitId ||
                "",

            appraisalDate:
                appraisal?.appraisalDate
                    ? String(
                        appraisal.appraisalDate
                    ).substring(0, 10)
                    : "",

            competitor:
                appraisal?.competitor ||
                "",

            discount:
                appraisal?.discount ||
                "",

            expectedOrder:
                appraisal?.expectedOrder ||
                "",

            decisionMaker:
                appraisal?.decisionMaker ||
                "",

            samplesGiven:
                appraisal?.samplesGiven !== null &&
                appraisal?.samplesGiven !== undefined
                    ? String(
                        appraisal.samplesGiven
                    )
                    : "",

            remarks:
                appraisal?.remarks ||
                "",

            followUpDate:
                appraisal?.followUpDate
                    ? String(
                        appraisal.followUpDate
                    ).substring(0, 10)
                    : "",

            books:
                Array.isArray(
                    appraisal?.books
                )
                    ? appraisal.books.map(
                        (book) => ({

                            bookName:
                                book.bookName ||
                                "",

                            isbn:
                                book.isbn ||
                                "",

                            discussed:
                                Boolean(
                                    book.discussed
                                ),

                            finalized:
                                Boolean(
                                    book.finalized
                                ),

                            quantity:
                                book.quantity ??
                                1,

                            unitPrice:
                                book.unitPrice ||
                                "",

                            discount:
                                book.discount ||
                                "",

                            remarks:
                                book.remarks ||
                                "",

                        })
                    )
                    : [],

        };

    };


    /*
    |--------------------------------------------------------------------------
    | INITIALIZE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!open) {
            return;
        }


        loadSchools();


        if (item) {

            const editForm =
                buildEditForm(
                    item
                );


            console.log(
                "APPRAISAL EDIT ITEM:",
                item
            );


            console.log(
                "APPRAISAL EDIT FORM:",
                editForm
            );


            setForm(
                editForm
            );

        } else {

            setForm({
                ...DEFAULT_FORM,

                /*
                Default date = today
                */

                appraisalDate:
                    new Date()
                        .toISOString()
                        .substring(
                            0,
                            10
                        ),

                books: [
                    {
                        ...EMPTY_BOOK,
                    },
                ],
            });

        }

    }, [
        open,
        item,
    ]);


    /*
    |--------------------------------------------------------------------------
    | INPUT CHANGE
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {

        const {
            name,
            value,
        } = e.target;


        setForm(
            (prev) => ({
                ...prev,
                [name]:
                    value,
            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | BOOK CHANGE
    |--------------------------------------------------------------------------
    */

    const handleBookChange = (
        index,
        field,
        value
    ) => {

        setForm(
            (prev) => {

                const books =
                    [...prev.books];


                books[index] = {
                    ...books[index],
                    [field]:
                        value,
                };


                return {
                    ...prev,
                    books,
                };

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | BOOK CHECKBOX
    |--------------------------------------------------------------------------
    */

    const handleBookCheckbox = (
        index,
        field,
        checked
    ) => {

        setForm(
            (prev) => {

                const books =
                    [...prev.books];


                books[index] = {
                    ...books[index],
                    [field]:
                        checked,
                };


                return {
                    ...prev,
                    books,
                };

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | ADD BOOK
    |--------------------------------------------------------------------------
    */

    const addBook = () => {

        setForm(
            (prev) => ({
                ...prev,

                books: [
                    ...prev.books,

                    {
                        ...EMPTY_BOOK,
                    },
                ],
            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | REMOVE BOOK
    |--------------------------------------------------------------------------
    */

    const removeBook = (
        index
    ) => {

        setForm(
            (prev) => {

                const books =
                    prev.books.filter(
                        (_, i) =>
                            i !== index
                    );


                return {
                    ...prev,

                    books:
                        books.length
                            ? books
                            : [
                                {
                                    ...EMPTY_BOOK,
                                },
                            ],
                };

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    const validateForm = () => {

        if (
            !form.schoolId
        ) {

            showError(
                "Please select a school."
            );

            return false;

        }


        if (
            !form.appraisalDate
        ) {

            showError(
                "Please select appraisal date."
            );

            return false;

        }


        if (
            !form.books ||
            !form.books.length
        ) {

            showError(
                "Please add at least one book."
            );

            return false;

        }


        for (
            let i = 0;
            i < form.books.length;
            i++
        ) {

            const book =
                form.books[i];


            if (
                !book.bookName?.trim()
            ) {

                showError(
                    `Please enter book name for book ${i + 1}.`
                );

                return false;

            }


            if (
                !book.quantity ||
                Number(
                    book.quantity
                ) < 1
            ) {

                showError(
                    `Please enter valid quantity for book ${i + 1}.`
                );

                return false;

            }

        }


        return true;

    };


    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (e) => {

            e.preventDefault();


            if (
                !validateForm()
            ) {

                return;

            }


            try {

                setLoading(
                    true
                );


                const payload = {

                    schoolId:
                        String(
                            form.schoolId
                        ),

                    visitId:
                        form.visitId
                            ? String(
                                form.visitId
                            )
                            : undefined,

                    appraisalDate:
                        form.appraisalDate,

                    competitor:
                        form.competitor ||
                        undefined,

                    discount:
                        form.discount ||
                        undefined,

                    expectedOrder:
                        form.expectedOrder ||
                        undefined,

                    decisionMaker:
                        form.decisionMaker ||
                        undefined,

                    samplesGiven:
                        form.samplesGiven !== ""
                            ? Number(
                                form.samplesGiven
                            )
                            : undefined,

                    remarks:
                        form.remarks ||
                        undefined,

                    followUpDate:
                        form.followUpDate ||
                        undefined,

                    books:
                        form.books.map(
                            (book) => ({

                                bookName:
                                    book.bookName.trim(),

                                isbn:
                                    book.isbn?.trim() ||
                                    undefined,

                                discussed:
                                    Boolean(
                                        book.discussed
                                    ),

                                finalized:
                                    Boolean(
                                        book.finalized
                                    ),

                                quantity:
                                    Number(
                                        book.quantity || 1
                                    ),

                                unitPrice:
                                    book.unitPrice ||
                                    undefined,

                                discount:
                                    book.discount ||
                                    undefined,

                                remarks:
                                    book.remarks?.trim() ||
                                    undefined,

                            })
                        ),

                };


                console.log(
                    "APPRAISAL PAYLOAD:",
                    payload
                );


                if (isEdit) {

                    await updateAppraisal(
                        item.id,
                        payload
                    );


                    showSuccess(
                        "Appraisal updated successfully."
                    );

                } else {

                    await createAppraisal(
                        payload
                    );


                    showSuccess(
                        "Appraisal created successfully."
                    );

                }


                await reload();

                onClose();

            } catch (error) {

                console.error(
                    "Appraisal save error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to save appraisal."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    /*
    |--------------------------------------------------------------------------
    | SCHOOL OPTIONS
    |--------------------------------------------------------------------------
    */

    const schoolOptions = [

        {
            value: "",
            label:
                schoolsLoading
                    ? "Loading Schools..."
                    : "Select School",
        },

        ...schools.map(
            (school) => ({

                value:
                    String(
                        school.id
                    ),

                label:
                    school.name ||
                    school.schoolName ||
                    school.schoolCode ||
                    `School ${school.id}`,

            })
        ),

    ];


    /*
    |--------------------------------------------------------------------------
    | CLOSED
    |--------------------------------------------------------------------------
    */

    if (!open) {
        return null;
    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <CrudModal

            open={
                open
            }

            title={
                isEdit
                    ? "Edit Appraisal"
                    : "Add Appraisal"
            }

            loading={
                loading
            }

            submitText={
                isEdit
                    ? "Update"
                    : "Save"
            }

            onClose={
                onClose
            }

            onSubmit={
                handleSubmit
            }

            width="max-w-7xl"

        >

            {/* =====================================================
                SCHOOL / DATE
            ====================================================== */}

            <div
                className="
                    mb-6
                    rounded-2xl
                    border
                    border-gray-200
                    bg-gray-50
                    p-5
                "
            >

                <h3
                    className="
                        mb-4
                        text-lg
                        font-bold
                        text-navy-700
                    "
                >
                    Appraisal Information
                </h3>


                <div
                    className="
                        grid
                        grid-cols-1
                        gap-5
                        md:grid-cols-2
                    "
                >

                    <CrudSelect
                        label="School"
                        name="schoolId"
                        value={
                            form.schoolId
                        }
                        onChange={
                            handleChange
                        }
                        options={
                            schoolOptions
                        }
                    />


                    <CrudInput
                        label="Appraisal Date"
                        type="date"
                        name="appraisalDate"
                        value={
                            form.appraisalDate
                        }
                        onChange={
                            handleChange
                        }
                    />

                </div>

            </div>


            {/* =====================================================
                COMMERCIAL
            ====================================================== */}

            <div
                className="
                    mb-6
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    p-5
                "
            >

                <h3
                    className="
                        mb-4
                        text-lg
                        font-bold
                        text-navy-700
                    "
                >
                    Commercial Details
                </h3>


                <div
                    className="
                        grid
                        grid-cols-1
                        gap-5
                        md:grid-cols-2
                        lg:grid-cols-3
                    "
                >

                    <CrudInput
                        label="Competitor"
                        name="competitor"
                        value={
                            form.competitor
                        }
                        onChange={
                            handleChange
                        }
                        placeholder="Competitor name"
                    />


                    <CrudInput
                        label="Discount (%)"
                        name="discount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                            form.discount
                        }
                        onChange={
                            handleChange
                        }
                        placeholder="10.00"
                    />


                    <CrudInput
                        label="Expected Order"
                        name="expectedOrder"
                        type="number"
                        step="0.01"
                        min="0"
                        value={
                            form.expectedOrder
                        }
                        onChange={
                            handleChange
                        }
                        placeholder="50000.00"
                    />


                    <CrudInput
                        label="Decision Maker"
                        name="decisionMaker"
                        value={
                            form.decisionMaker
                        }
                        onChange={
                            handleChange
                        }
                        placeholder="Decision maker name"
                    />


                    <CrudInput
                        label="Samples Given"
                        name="samplesGiven"
                        type="number"
                        min="0"
                        value={
                            form.samplesGiven
                        }
                        onChange={
                            handleChange
                        }
                        placeholder="0"
                    />


                    <CrudInput
                        label="Follow-up Date"
                        name="followUpDate"
                        type="date"
                        value={
                            form.followUpDate
                        }
                        onChange={
                            handleChange
                        }
                    />

                </div>

            </div>


            {/* =====================================================
                BOOKS
            ====================================================== */}

            <div
                className="
                    mb-6
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    p-5
                "
            >

                <div
                    className="
                        mb-5
                        flex
                        items-center
                        justify-between
                    "
                >

                    <div>

                        <h3
                            className="
                                text-lg
                                font-bold
                                text-navy-700
                            "
                        >
                            Books
                        </h3>

                        <p
                            className="
                                mt-1
                                text-xs
                                text-gray-500
                            "
                        >
                            Add books discussed
                            with the school and
                            mark finalized books.
                        </p>

                    </div>


                    <button
                        type="button"
                        onClick={
                            addBook
                        }
                        className="
                            rounded-xl
                            bg-brand-500
                            px-4
                            py-2.5
                            text-sm
                            font-semibold
                            text-white
                        "
                    >
                        + Add Book
                    </button>

                </div>


                <div
                    className="
                        space-y-5
                    "
                >

                    {form.books.map(
                        (
                            book,
                            index
                        ) => (

                            <div
                                key={
                                    index
                                }
                                className="
                                    rounded-2xl
                                    border
                                    border-gray-200
                                    bg-gray-50
                                    p-5
                                "
                            >

                                {/* BOOK HEADER */}

                                <div
                                    className="
                                        mb-4
                                        flex
                                        items-center
                                        justify-between
                                    "
                                >

                                    <h4
                                        className="
                                            font-semibold
                                            text-gray-800
                                        "
                                    >
                                        Book #
                                        {index + 1}
                                    </h4>


                                    {form.books.length >
                                        1 && (

                                        <button
                                            type="button"
                                            onClick={() =>
                                                removeBook(
                                                    index
                                                )
                                            }
                                            className="
                                                text-sm
                                                font-medium
                                                text-red-500
                                            "
                                        >
                                            Remove
                                        </button>

                                    )}

                                </div>


                                {/* BOOK INPUTS */}

                                <div
                                    className="
                                        grid
                                        grid-cols-1
                                        gap-4
                                        md:grid-cols-2
                                        lg:grid-cols-3
                                    "
                                >

                                    <CrudInput
                                        label="Book Name"
                                        value={
                                            book.bookName
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            handleBookChange(
                                                index,
                                                "bookName",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter book name"
                                    />


                                    <CrudInput
                                        label="ISBN"
                                        value={
                                            book.isbn
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            handleBookChange(
                                                index,
                                                "isbn",
                                                e.target.value
                                            )
                                        }
                                        placeholder="ISBN"
                                    />


                                    <CrudInput
                                        label="Quantity"
                                        type="number"
                                        min="1"
                                        value={
                                            book.quantity
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            handleBookChange(
                                                index,
                                                "quantity",
                                                e.target.value
                                            )
                                        }
                                    />


                                    <CrudInput
                                        label="Unit Price"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                            book.unitPrice
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            handleBookChange(
                                                index,
                                                "unitPrice",
                                                e.target.value
                                            )
                                        }
                                        placeholder="450.00"
                                    />


                                    <CrudInput
                                        label="Book Discount (%)"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={
                                            book.discount
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            handleBookChange(
                                                index,
                                                "discount",
                                                e.target.value
                                            )
                                        }
                                        placeholder="10.00"
                                    />


                                    <CrudInput
                                        label="Book Remarks"
                                        value={
                                            book.remarks
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            handleBookChange(
                                                index,
                                                "remarks",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Remarks"
                                    />

                                </div>


                                {/* DISCUSSED / FINALIZED */}

                                <div
                                    className="
                                        mt-5
                                        flex
                                        flex-wrap
                                        gap-6
                                    "
                                >

                                    <label
                                        className="
                                            flex
                                            cursor-pointer
                                            items-center
                                            gap-2
                                            text-sm
                                            font-medium
                                            text-gray-700
                                        "
                                    >

                                        <input
                                            type="checkbox"
                                            checked={
                                                Boolean(
                                                    book.discussed
                                                )
                                            }
                                            onChange={(
                                                e
                                            ) =>
                                                handleBookCheckbox(
                                                    index,
                                                    "discussed",
                                                    e.target.checked
                                                )
                                            }
                                            className="
                                                h-4
                                                w-4
                                            "
                                        />

                                        Books Discussed

                                    </label>


                                    <label
                                        className="
                                            flex
                                            cursor-pointer
                                            items-center
                                            gap-2
                                            text-sm
                                            font-medium
                                            text-gray-700
                                        "
                                    >

                                        <input
                                            type="checkbox"
                                            checked={
                                                Boolean(
                                                    book.finalized
                                                )
                                            }
                                            onChange={(
                                                e
                                            ) =>
                                                handleBookCheckbox(
                                                    index,
                                                    "finalized",
                                                    e.target.checked
                                                )
                                            }
                                            className="
                                                h-4
                                                w-4
                                            "
                                        />

                                        Book Finalized

                                    </label>

                                </div>

                            </div>

                        )
                    )}

                </div>

            </div>


            {/* =====================================================
                REMARKS
            ====================================================== */}

            <div
                className="
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    p-5
                "
            >

                <CrudInput
                    label="Remarks"
                    name="remarks"
                    value={
                        form.remarks
                    }
                    onChange={
                        handleChange
                    }
                    placeholder="Enter appraisal remarks"
                />

            </div>

        </CrudModal>

    );

}